/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { Message, MessageType } from 'CalltakingCoreApi';
import { filter, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectMessageEffectInitialized, selectMultipleMessageStateInitializationTime } from '../+state/message.selectors';
import { updateMultipleMessageSubscriptions } from '../+state/message.actions';
import { StompBaseConsumer } from '../../core/abstract/stomp-base-consumer';
import { StompManagerService } from '../../core/services/stomp-manager/stomp-manager.service';
import { selectClusterConfigurationMap } from '../../configuration/+state/configuration.selectors';
import { Topic } from '../../user/model/multiple-subscriptions';

export abstract class MessageService extends StompBaseConsumer {
    protected type: MessageType;
    protected API_BASE_URL = '/api/imc/v1/';

    protected constructor(type: MessageType, store: Store, stompManager: StompManagerService) {
        super(stompManager, store);
        this.type = type;
        store.select(selectMessageEffectInitialized).pipe(filter((init) => init)).subscribe(() => this.effectsInitialized());
            // Trigger (re)initialization of each cluster's message state
            store
                .select(selectClusterConfigurationMap)
                .pipe(
                    filter((clusterMap) => Boolean(Object.keys(clusterMap).length)),
                    take(1)
                )
                .subscribe((clusterMap) =>
                    Object.keys(clusterMap).forEach((clusterName) =>
                        this.store
                            .select(selectMultipleMessageStateInitializationTime(clusterName))
                            .pipe(filter((init) => !!init))
                            .subscribe(() => this.websocketInitialized(clusterName))
                    )
                );
    }

    handleSubscriptionInit(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Message topic ${topic} subscription success: ${receiptId}`);
        this.store.dispatch(updateMultipleMessageSubscriptions({ topic: topic as Topic, clusterName, status: true }));
    }

    handleSubscriptionEnd(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Message topic ${topic} subscription end: ${receiptId}`);
        this.store.dispatch(updateMultipleMessageSubscriptions({ topic: topic as Topic, clusterName, status: false }));
    }

    handleStompError(topic: string, receiptId: string, error: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Message topic ${topic} subscription failed=${error} for receiptId=${receiptId}`);
    }

    protected abstract newMessage(message: Message): void;

    protected abstract editMessage(message: Message): void;

    protected abstract deleteMessage(message: Message): void;

    protected abstract effectsInitialized(): void;

    protected abstract websocketInitialized(clusterName?: string): void;
}
